import { useEffect, useState } from "react";
import { Sparkles, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";

// Bump `version` and edit `items` with each update; everyone sees the card once per version.
export const WHATS_NEW = {
    version: "2026-11-04",
    items: [
        { text: "New reports: NHF remittance schedule and HMO deductions by provider — PDF and Excel.", admin: true },
        { text: "Money boxes now show ₦ and commas as you type, e.g. ₦ 152,350.75 — kobo allowed." },
        { text: "Loans: tap the calendar icon on any loan to see its month-by-month repayment plan." },
        { text: "Payslips now include a tax breakdown: taxable pay, pension base, reliefs and loan deductions." },
        { text: "Optional NHF (2.5% of basic) and HMO deductions per employee.", admin: true },
        { text: "Attendance summary on payslips can be switched off in Settings.", admin: true },
        { text: "Loans can start in any month that isn't finalised; Recalc PAYE also refreshes loan deductions.", admin: true },
        { text: "The app now updates itself after each release — no reinstall needed." },
    ],
};

const KEY = "hrm_whats_new_seen";

export default function WhatsNew() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!user || localStorage.getItem(KEY) === WHATS_NEW.version) return;
        // Wait until no other pop-up (e.g. the welcome card) is open.
        const t = setInterval(() => {
            if (!document.querySelector('[role="dialog"], [role="alertdialog"]')) { setOpen(true); clearInterval(t); }
        }, 2000);
        return () => clearInterval(t);
    }, [user]);
    const close = () => { localStorage.setItem(KEY, WHATS_NEW.version); setOpen(false); };
    const admin = hasRole(user, "admin", "hr", "accountant");
    const items = WHATS_NEW.items.filter((i) => !i.admin || admin);
    return (
        <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
            <DialogContent className="max-w-md" data-testid="whats-new-dialog">
                <DialogHeader>
                    <div className="w-11 h-11 rounded-2xl bg-klein text-white flex items-center justify-center mb-2"><Sparkles className="w-5 h-5" /></div>
                    <DialogTitle className="font-display text-2xl">What's new</DialogTitle>
                    <DialogDescription>The latest improvements in Suntosh HRM.</DialogDescription>
                </DialogHeader>
                <ul className="space-y-2.5" data-testid="whats-new-list">
                    {items.map((i) => (
                        <li key={i.text} className="flex gap-2.5 text-sm text-ink/80"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />{i.text}</li>
                    ))}
                </ul>
                <button onClick={close} className="mt-2 w-full bg-klein text-white py-2.5 text-sm hover:bg-klein/90" data-testid="whats-new-close">Got it</button>
            </DialogContent>
        </Dialog>
    );
}
